/**
 * WebSocket context provider for sharing connection state across the app.
 * Provides real-time task notifications for contractors.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  useWebSocket,
  type ConnectionState,
  type WebSocketMessage,
} from '../hooks/useWebSocket';

interface WebSocketContextValue {
  connectionState: ConnectionState;
  connected: boolean;
  isOnline: boolean;
  lastMessage: WebSocketMessage | null;
  lastConnectedAt: Date | null;
  reconnect: () => void;
  hasNewAssignment: boolean;
  clearNewAssignment: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [hasNewAssignment, setHasNewAssignment] = useState(false);

  const handleTaskAssigned = useCallback(() => {
    setHasNewAssignment(true);
  }, []);

  const clearNewAssignment = useCallback(() => {
    setHasNewAssignment(false);
  }, []);

  const {
    connectionState,
    connected,
    isOnline,
    lastMessage,
    lastConnectedAt,
    reconnect,
  } = useWebSocket({
    onTaskAssigned: handleTaskAssigned,
  });

  return (
    <WebSocketContext.Provider
      value={{
        connectionState,
        connected,
        isOnline,
        lastMessage,
        lastConnectedAt,
        reconnect,
        hasNewAssignment,
        clearNewAssignment,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error(
      'useWebSocketContext must be used within a WebSocketProvider'
    );
  }
  return context;
}
